import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Shield, UserCheck, MapPin, Handshake, Target, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { whyChooseUs, processSteps } from '../utils/mockData';

import usePageSeo from '../hooks/usePageSeo';
const WhyChooseUs = () => {
  usePageSeo({
    title: 'Why Choose WPAA | UAE Audit & Tax Firm',
    description: 'Discover why UAE businesses choose WPAA — 18+ years expertise, end-to-end services, transparent pricing and 98% client retention.',
    path: '/why-choose-us'
  });

  const iconMap = {
    DollarSign, Shield, UserCheck, MapPin, Handshake, Target
  };

  const clientBenefits = [
    'Transparent Reporting',
    'Accurate Results',
    'Secure Data Handling',
    'Timely Delivery',
    'Customized Solutions',
    'Responsive Communication'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto text-center">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">The White Paper Difference</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Why Businesses Trust White Paper
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Combining competitive pricing with uncompromising quality, we deliver exceptional value and build lasting partnerships with businesses across the UAE.
          </p>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Six core strengths that make White Paper the preferred choice for audit, tax, and advisory services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                      <Icon className="text-teal-600 group-hover:text-white transition-colors" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client Benefits */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Client Benefits</h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                When you partner with White Paper, you gain access to a comprehensive suite of benefits designed to support your business success.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {clientBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2 p-3 bg-white rounded-lg hover:bg-teal-50 transition-colors">
                    <CheckCircle className="text-teal-600 flex-shrink-0" size={20} />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Client success"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How We Work
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A transparent, client-focused process ensuring quality delivery at every stage.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <Card className="border-2 hover:border-teal-500 transition-all h-full">
                    <CardContent className="p-6">
                      <div className="text-6xl font-bold text-teal-100 mb-4">{step.number}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </CardContent>
                  </Card>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 z-10">
                      <ArrowRight className="text-teal-300" size={24} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Track Record</h2>
            <p className="text-teal-100 text-lg max-w-2xl mx-auto">
              Numbers that demonstrate our commitment to excellence and client success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '200+', label: 'Clients Served', sublabel: 'Across diverse industries' },
              { value: '98%', label: 'Client Retention', sublabel: 'Industry-leading loyalty' },
              { value: '100%', label: 'Compliance Rate', sublabel: 'Perfect track record' },
              { value: '18+', label: 'Years Experience', sublabel: 'Combined team expertise' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 hover:bg-white/20 transition-all">
                  <p className="text-5xl font-bold mb-2">{stat.value}</p>
                  <p className="text-xl font-semibold mb-1">{stat.label}</p>
                  <p className="text-sm text-teal-100">{stat.sublabel}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              White Paper vs. Others
            </h2>

            <div className="space-y-4">
              {[
                { feature: 'Competitive Pricing', us: true, others: false },
                { feature: 'Senior Professional Access', us: true, others: false },
                { feature: 'UAE Market Expertise', us: true, others: 'Varies' },
                { feature: 'Personalized Service', us: true, others: 'Limited' },
                { feature: 'Long-term Partnership Focus', us: true, others: false },
                { feature: 'Transparent Communication', us: true, others: 'Varies' }
              ].map((row, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                  <div className="font-medium text-gray-900">{row.feature}</div>
                  <div className="text-center">
                    {row.us === true ? (
                      <CheckCircle className="text-teal-600 mx-auto" size={24} />
                    ) : (
                      <span className="text-gray-600">{row.us}</span>
                    )}
                  </div>
                  <div className="text-center text-gray-500">
                    {row.others === true ? (
                      <CheckCircle className="text-gray-400 mx-auto" size={24} />
                    ) : row.others === false ? (
                      <span className="text-red-500">✗</span>
                    ) : (
                      <span>{row.others}</span>
                    )}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-4 p-2 font-bold text-sm text-gray-600">
                <div>Feature</div>
                <div className="text-center text-teal-600">White Paper</div>
                <div className="text-center">Others</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Highlight */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-12">
            <div className="flex mb-6">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-2xl text-gray-800 italic mb-6 leading-relaxed">
              "White Paper transformed our financial management. Their expertise saved us penalty on taxes while ensuring complete compliance. The team's professionalism and personalized approach exceeded all expectations."
            </p>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-teal-600 font-bold text-2xl">S</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Shibin S</p>
                <p className="text-gray-600">Managing Director, FinLedge</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Experience the White Paper Difference</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the growing number of businesses that trust us with their financial success.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg group">
              Start Your Journey Today
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
